import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { shuffle } from 'lodash'; // Importing the shuffle function from a library like Lodash
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAudioRecorder } from '../../AudioRecorder';
import { DialogPromptQuizQuestionSpeak } from '../../api/DialogPromptApi';
import { ISentenceScore, ScoreApi } from '../../api/ScoreApi';
import { UserDialogPromptApi } from '../../api/UserDialogPromptApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { SentenceScoreMediumResult, SentenceScoreTryAgain, SentenceScoreTryAgainNo, SentenceScoreTryAgainYes, getAudioByText } from './DialogPromptComponent';

import { ScoreGrade, hasAnySpokenSentenceScore, sentenceScoreAllGood, sentenceScoreNonSilentFailed, sentenceScoreAnyFailed } from '../../Score';
import AddSelfStudyBlueIcon from '../../asset/icon-add-self-study-blue.png';
import MicrophoneBlueIcon from '../../asset/icon-microphone-blue.png';
import RightArrowBlueIcon from '../../asset/icon-right-arrow-blue.png';
import AudioWaveIcon from '../../asset/icons8-audio-wave.gif';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import AddSentenceDialog from '../AddSentenceDialog';

interface DialogPromptQuizQuestionSpeakComponentProps {
    dialogPrompt: DialogPromptQuizQuestionSpeak;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

type AnswerOption = {
    id: number;
    word?: string;
    word_pinyin?: string[];
    image_url?: string | null;
};

const SpeakAnswerCard: React.FC<{
    answerOption: AnswerOption;
    onSelect: (answerOption: AnswerOption, correct: boolean) => void;
    isCorrect: (id: number) => boolean;
}> = ({ answerOption, onSelect, isCorrect }) => (
    <Stack key={answerOption.id} direction="column" alignItems="center">
        {!!answerOption.word && (
            <Button
                variant="contained"
                onClick={() => onSelect(answerOption, isCorrect(answerOption.id))}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography style={{ fontFamily: 'KaiTi', fontSize: '24px' }}>
                        {answerOption.word}
                    </Typography>
                    {answerOption.word_pinyin && !!answerOption.word_pinyin.length && (
                        <Typography style={{ fontSize: '12px' }}>
                            {answerOption.word_pinyin.join(' ')}
                        </Typography>
                    )}
                </div>
            </Button>
        )}
        {!!answerOption.image_url && (
            <img
                src={answerOption.image_url}
                alt={answerOption.word || ''}
                onClick={() => onSelect(answerOption, isCorrect(answerOption.id))}
                style={{ maxHeight: '125px', maxWidth: '125px', height: 'auto', width: 'auto' }}
            />
        )}
    </Stack>
);

const DialogPromptQuizQuestionSpeakComponent: React.FC<DialogPromptQuizQuestionSpeakComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef, isSettings = false }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();


    const { setLeftNavigationList, setRightNavigationList, setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [componentList, setComponentList] = React.useState<any[]>([]);
    const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
    const [studentCorrectAnswerDone, setStudentCorrectAnswerDone] = React.useState(false)
    const [chatBubbleCorrectDone] = React.useState(false);


    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setTeacherChinesePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [noResponseAudio, setNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherAskStudentToSpeakPromptAudio, setTeacherAskStudentToSpeakPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [correctVocabularyWordAudioSentenceAudio, setCorrectVocabularyWordAudioSentenceAudio] = React.useState<Blob | null | undefined>(undefined);
    const [spokenYesResponseAudio, setSpokenYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [spokenNoResponseAudio, setSpokenNoResponseAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreMediumResultAudio, setSentenceScoreMediumResultAudio] = React.useState<Blob | null | undefined>(undefined);
    const [sentenceScoreTryAgainAudio, setSentenceScoreTryAgainAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainYesAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setSentenceScoreTryAgainNoAudio] = React.useState<Blob | null | undefined>(undefined);

    const [shuffledAnswerOptionIdList, setShuffledAnswerOptionIdList] = React.useState<number[]>([]);

    // Build options from dialogPrompt (correct + incorrect)
    const answerOptionList: AnswerOption[] = React.useMemo(() => {
        const word_list = [
            ...dialogPrompt.incorrect_vocabulary_word_list,
            dialogPrompt.correct_vocabulary_word,
        ];
        return word_list.map(word => ({
            id: word.id,
            word: word.word,
            word_pinyin: word.word_pinyin,
            image_url: word.image_url ? word.image_url : null,
        }));
    }, [dialogPrompt]);


    const answerOptionByIdMap = React.useMemo(() => {
        const map = new Map<number, AnswerOption>();
        answerOptionList.forEach(answerOption => map.set(answerOption.id, answerOption));
        return map;
    }, [answerOptionList]);

    React.useEffect(() => {
        const answerOptionIdList = answerOptionList.map(o => o.id);
        setShuffledAnswerOptionIdList(shuffle(answerOptionIdList));
    }, [answerOptionList])

    React.useEffect(() => {
        clearFooterList()
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_chinese_prompt, setTeacherChinesePromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.no_response, setNoResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_ask_student_to_speak_prompt, setTeacherAskStudentToSpeakPromptAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.correct_vocabulary_word.audio_sentence, setCorrectVocabularyWordAudioSentenceAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.spoken_yes_response, setSpokenYesResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, dialogPrompt.spoken_no_response, setSpokenNoResponseAudio, isSettings)
        getAudioByText(history, setErrorMessage, SentenceScoreMediumResult, setSentenceScoreMediumResultAudio, isSettings)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgain, setSentenceScoreTryAgainAudio, isSettings)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainYes, setSentenceScoreTryAgainYesAudio, isSettings)
        getAudioByText(history, setErrorMessage, SentenceScoreTryAgainNo, setSentenceScoreTryAgainNoAudio, isSettings)
    }, [
        clearFooterList, dialogPrompt.prompt, dialogPrompt.teacher_chinese_prompt,
        dialogPrompt.yes_response, dialogPrompt.no_response, dialogPrompt.teacher_ask_student_to_speak_prompt,
        dialogPrompt.correct_vocabulary_word.audio_sentence, dialogPrompt.spoken_yes_response,
        dialogPrompt.spoken_no_response, history, isSettings, setErrorMessage,
    ]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [componentList, chatBubbleContainerRef])

    const isCorrect = React.useCallback((id: number) => id === dialogPrompt.correct_vocabulary_word.id, [dialogPrompt.correct_vocabulary_word.id]);

    const handleAnswerClickRef = React.useRef<(answerOption: AnswerOption, correct: boolean) => void>(() => {});

    const handleAnswerClick = React.useCallback((answerOption: AnswerOption, correct: boolean) => {
        const additionalComponentList: React.ReactNode[] = []
        additionalComponentList.push(<SpeakAnswerCard answerOption={answerOption} onSelect={() => { }} isCorrect={isCorrect} key={`student-img-${answerOption.id}`} />)
        if (correct) {
            clearFooterList()
            const newChatBubble = (
                <ChatBubble
                    key={`yes-${answerOption.id}`}
                    isTeacher={true}
                    message={dialogPrompt.yes_response}
                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setStudentCorrectAnswerDone}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, ...additionalComponentList, newChatBubble]);
            UserDialogPromptApi.addCorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        } else {
            // reshuffle ids
            const newIds = shuffle(shuffledAnswerOptionIdList.slice());
            setShuffledAnswerOptionIdList(newIds);
            const newFooterList = newIds.map(id => (
                <SpeakAnswerCard
                    key={id}
                    answerOption={answerOptionByIdMap.get(id)!}
                    onSelect={handleAnswerClickRef.current}
                    isCorrect={isCorrect}
                />
            ));
            setActionList(newFooterList);

            const newChatBubble = (
                <ChatBubble
                    key={`no-${answerOption.id}`}
                    isTeacher={true}
                    message={dialogPrompt.no_response}
                    messageAudio={noResponseAudio ? noResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                />
            );
            setComponentList((prevComponentList) => [...prevComponentList, ...additionalComponentList, newChatBubble]);
            UserDialogPromptApi.addIncorrectAnswer(dialogPrompt.id)
                .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
        }
    }, [isCorrect, clearFooterList, dialogPrompt.yes_response, dialogPrompt.no_response, dialogPrompt.id, yesResponseAudio, noResponseAudio, showAvatar, chatBubbleContainerRef, shuffledAnswerOptionIdList, answerOptionByIdMap, setActionList, setErrorMessage, history, location]);

    handleAnswerClickRef.current = handleAnswerClick;

    React.useEffect(() => {
        if (chatBubbleDone) {
            const shuffledAnswerList = shuffledAnswerOptionIdList.map(id => {
                const answerOption = answerOptionByIdMap.get(id)!;
                return <SpeakAnswerCard key={id} answerOption={answerOption} onSelect={handleAnswerClick} isCorrect={isCorrect} />;
            })

            setActionList(shuffledAnswerList);
        }
    }, [chatBubbleDone, shuffledAnswerOptionIdList, answerOptionByIdMap, setActionList, handleAnswerClick, isCorrect]);





    const [addSentenceDialogOpen, setAddSentenceDialogOpen] = React.useState(false);
    const [, setAddSentenceSuccessMessage] = React.useState('');

    const handleAddSentenceClick = () => {
        setAddSentenceDialogOpen(true);
    };


    const handleAddSentenceClose = () => {
        setAddSentenceDialogOpen(false);
        setAddSentenceSuccessMessage('')
    };

    const [isScoring, setIsScoring] = React.useState(false);
    const [sentenceScoreList, setSentenceScoreList] = React.useState<ISentenceScore[]>([]);
    const [chatBubbleSentenceScoreDone, setChatBubbleSentenceScoreDone] = React.useState<boolean[]>([]);
    const [chatBubbleTeacherResponseDone, setChatBubbleTeacherResponseDone] = React.useState<boolean[]>([]);
    const [tryAgainClickedList, setTryAgainClickedList] = React.useState<(boolean | null)[]>([]);

    const [teacherSpeakRequestDone, setTeacherSpeakRequestDone] = React.useState(false);

    const [chatBubbleTryAgainDone, setChatBubbleTryAgainDone] = React.useState(false);
    const [chatBubbleContinueDone, setChatBubbleContinueDone] = React.useState(false);


    const { startRecording, stopRecording, isRecording } = useAudioRecorder({
        setErrorMessage,
        onStop: (audioData) => {
            setIsScoring(true)
            const formData = new FormData();
            formData.append('audio', audioData);

            ScoreApi.getScore(dialogPrompt.correct_vocabulary_word.audio_sentence_id, formData)
                .then(response => {
                    setIsScoring(false)
                    // check if the response was successful
                    if (response.status >= 300) {
                        throw new Error('Scoring audio was not ok statuscode=' + response.status + " " + response.statusText);
                    }
                    return response.data;
                })
                .then(responseJson => {
                    UserDialogPromptApi.addSpeakingAttempt(dialogPrompt.id, responseJson.score_mean)
                        .catch(err => { handleErrorMessage(err, setErrorMessage, history, location); })
                    responseJson.audioData = audioData;
                    setSentenceScoreList((previous) => [...previous, responseJson]);
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    });

    const [shouldJiggleSkipArrow, setShouldJiggleSkipArrow] = React.useState(false);
    const jiggleKeyframes = React.useMemo(() => ({
        '@keyframes jiggle': {
            '0%': { transform: 'rotate(0deg)' },
            '12.5%': { transform: 'rotate(-20deg)' },
            '37.5%': { transform: 'rotate(20deg)' },
            '52.5%': { transform: 'rotate(-20deg)' },
            '77.5%': { transform: 'rotate(20deg)' },
            '100%': { transform: 'rotate(0)' },
        },
    }), []);
    const jiggleDuration = 500
    const jiggleIteration = 8
    React.useEffect(() => {
        if (sentenceScoreList.length === 0) return;

        const last = sentenceScoreList[sentenceScoreList.length - 1];
        if (!sentenceScoreNonSilentFailed(last)) {
            setShouldJiggleSkipArrow(false);
            return;
        }

        const failedCount = sentenceScoreList.reduce(
            (acc, sentenceScore) => acc + (sentenceScoreNonSilentFailed(sentenceScore) ? 1 : 0),
            0
        );

        if (failedCount % 2 === 0) {
            setShouldJiggleSkipArrow(true);
            const totalDuration = jiggleDuration * jiggleIteration
            const t = setTimeout(() => setShouldJiggleSkipArrow(false), totalDuration + 50); // length of the animation
            return () => clearTimeout(t);
        } else {
            setShouldJiggleSkipArrow(false);
        }
    }, [sentenceScoreList]);

    React.useEffect(() => {
        if (studentCorrectAnswerDone && chatBubbleTryAgainDone == false) {
            if (sentenceScoreList.length == 0 || sentenceScoreList[sentenceScoreList.length - 1].passing_grade == ScoreGrade.Bad || tryAgainClickedList[sentenceScoreList.length - 1]) {
                if (hasAnySpokenSentenceScore(sentenceScoreList)) {
                    setLeftNavigationList([
                        <IconButton
                            onClick={handleCancelClick}
                            sx={{
                                ...jiggleKeyframes,
                                animation: shouldJiggleSkipArrow ? `jiggle ${jiggleDuration}ms ease-in-out ${jiggleIteration}` : 'none',
                            }}
                        >
                            <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                        </IconButton>
                    ])
                } else {
                    setLeftNavigationList([])
                }
                setRightNavigationList([
                    <Tooltip title="Add to Pronunciation" arrow>
                        <IconButton onClick={handleAddSentenceClick} sx={{ alignSelf: 'center' }}>
                            <img src={AddSelfStudyBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                        </IconButton>
                    </Tooltip>
                ])
                setActionList([
                    <IconButton onClick={isRecording ? stopRecording : startRecording}>
                        {isRecording ? <img src={AudioWaveIcon} alt="Recording" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                            : isScoring ? <CircularProgress size={24} sx={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                                : <img src={MicrophoneBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />}
                    </IconButton>
                ])
            }
        }
    }, [sentenceScoreList, isRecording, isScoring, teacherSpeakRequestDone, chatBubbleTryAgainDone, shouldJiggleSkipArrow, studentCorrectAnswerDone, jiggleKeyframes, setLeftNavigationList, setRightNavigationList, setActionList, startRecording, stopRecording, tryAgainClickedList]);

    React.useEffect(() => {
        if (chatBubbleContainerRef.current) {
            scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
        }
    }, [sentenceScoreList, chatBubbleContainerRef]);


    const updateTryAgainClickedList = React.useCallback((tryAgain: boolean) => {
        const index = sentenceScoreList.length - 1;
        setTryAgainClickedList(previous => {
            const updatedArray = [...previous];
            updatedArray[index] = tryAgain;
            return updatedArray;
        })
    }, [sentenceScoreList.length]);

    const handleNextWordClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(false)
        setChatBubbleContinueDone(true)
    }, [updateTryAgainClickedList]);

    const handleTryAgainClick = React.useCallback(() => {
        setChatBubbleTryAgainDone(false)
        updateTryAgainClickedList(true)
    }, [updateTryAgainClickedList]);

    React.useEffect(() => {
        if (chatBubbleTryAgainDone) {
            setLeftNavigationList([
                <IconButton onClick={handleCancelClick}>
                    <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
                </IconButton>
            ])
            setActionList([
                <Button variant="contained" onClick={handleNextWordClick} className='secondaryButton'>
                    No, move on
                </Button>,
                <Button variant="contained" onClick={handleTryAgainClick} >
                    Yes, try again
                </Button>
            ])
        }
    }, [chatBubbleTryAgainDone, handleNextWordClick, handleTryAgainClick, setLeftNavigationList, setActionList]);

    const [openCancelDialog, setOpenCancelDialog] = React.useState(false);

    const handleCancelClick = () => {
        setOpenCancelDialog(true);
    };

    const [, setClosestSentencePromptDone] = React.useState(false);

    const handleCancelDialogConfirm = () => {
        setOpenCancelDialog(false);
        setChatBubbleContinueDone(true);
        // if (studentCorrectAnswerDone || teacherSpeakRequestDone || closestSentencePromptDone) {
        //     // we're past the typing, so we're in the speaking. skip the remainder of the exercise
        //     setChatBubbleContinueDone(true);
        // } else if (correctedResponse) {
        //     // didn't pass typing, but did they try at all?
        //     // then only skip to typing part
        //     setTeacherChineseChatBubbleDone(true);
        //     setAnswerSubmitted(true);
        //     setStudentAnswerDone(true);
        // } else {
        //     // we're somewhere in the beginning? skip only the typing part
        //     const response = dialogPrompt.dialog_prompt_sentence_list[0].student_response;
        //     if (response === undefined || response === null) {
        //         throw Error("Empty dialogPrompt.dialog_prompt_sentence_list[0].student_response");
        //     }
        //     setCorrectedResponse(new CorrectedResponse(false, "Please speak this sentence.", response, response, dialogPrompt.dialog_prompt_sentence_list[0].id))
        //     setStudentAnswerDone(true);
        // }
    };

    const handleCancelDialogCancel = () => {
        setOpenCancelDialog(false);
    };

    React.useEffect(() => {
        if (chatBubbleContinueDone) {
            clearFooterList()
            setDialogPromptComplete(dialogPrompt.id)

        }
    }, [chatBubbleContinueDone, dialogPrompt.id, clearFooterList, setDialogPromptComplete])

    React.useEffect(() => {
        if (chatBubbleCorrectDone) {
            setDialogPromptComplete(dialogPrompt.id)
        }
    }, [chatBubbleCorrectDone, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack spacing={2}>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                setChatBubbleDone={setChatBubbleDone}
                chatBubbleContainerRef={chatBubbleContainerRef}
            />
            {componentList.map((component, index) => (
                <Box key={index} component="div">
                    {component}
                </Box>
            ))}

            {studentCorrectAnswerDone && (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.teacher_ask_student_to_speak_prompt ? dialogPrompt.teacher_ask_student_to_speak_prompt : ""}
                    messageAudio={teacherAskStudentToSpeakPromptAudio ? teacherAskStudentToSpeakPromptAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setTeacherSpeakRequestDone}
                />
            )}
            {teacherSpeakRequestDone && (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.correct_vocabulary_word.audio_sentence}
                    messageAudio={correctVocabularyWordAudioSentenceAudio ? correctVocabularyWordAudioSentenceAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setClosestSentencePromptDone}
                    listenSentenceId={dialogPrompt.correct_vocabulary_word.audio_sentence_id}
                />
            )}

            {sentenceScoreList.map((sentenceScore, index) => (
                <div key={index}>
                    <ChatBubble
                        isTeacher={false}
                        message={sentenceScore.text}
                        messageAudio={null} // audio will be handled through sentence score
                        sentenceScore={sentenceScore}
                        showAvatar={showAvatar}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                        setChatBubbleDone={(done) => setChatBubbleSentenceScoreDone(previous => {
                            // console.log("sentene score updated index " + index)
                            const updatedArray = [...previous];
                            updatedArray[index] = done;
                            return updatedArray;
                        })}
                    />
                    {chatBubbleSentenceScoreDone[index] && (<>
                        {sentenceScoreAllGood(sentenceScore) ? (
                            <ChatBubble
                                isTeacher={true}
                                message={dialogPrompt.spoken_yes_response ? dialogPrompt.spoken_yes_response : ""}
                                messageAudio={spokenYesResponseAudio ? spokenYesResponseAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleContinueDone}
                            />
                        ) : sentenceScoreAnyFailed(sentenceScore) ? (
                            <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                message={dialogPrompt.spoken_no_response ? dialogPrompt.spoken_no_response : ""}
                                messageAudio={spokenNoResponseAudio ? spokenNoResponseAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={() => {
                                    setChatBubbleTryAgainDone(false);
                                    // set null, to skip this index
                                    setTryAgainClickedList((previous) => [...previous, null]);
                                }}
                            />
                        ) : (
                            <ChatBubble isTeacher={dialogPrompt.is_teacher}
                                message={SentenceScoreMediumResult}
                                messageAudio={sentenceScoreMediumResultAudio ? sentenceScoreMediumResultAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={(done) => setChatBubbleTeacherResponseDone(previous => {
                                    const updatedArray = [...previous];
                                    updatedArray[index] = done;
                                    return updatedArray;
                                })}
                            />
                        )}
                        {chatBubbleTeacherResponseDone[index] && (
                            <ChatBubble
                                isTeacher={true}
                                message={SentenceScoreTryAgain}
                                messageAudio={sentenceScoreTryAgainAudio ? sentenceScoreTryAgainAudio : null}
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleTryAgainDone}
                            />
                        )}
                        {tryAgainClickedList[index] === true && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainYes}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                            />
                        )}
                        {tryAgainClickedList[index] === false && (
                            <ChatBubble
                                isTeacher={false}
                                message={SentenceScoreTryAgainNo}
                                messageAudio={null} // no audio for student
                                showAvatar={showAvatar}
                                chatBubbleContainerRef={chatBubbleContainerRef}
                                setChatBubbleDone={setChatBubbleContinueDone}
                            />

                        )}
                    </>)}
                </div>
            ))}
            {/* Confirmation Dialog */}
            <Dialog open={openCancelDialog} onClose={handleCancelDialogCancel}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Confirm Skip</Typography></DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#8D8D8D' }}>
                        Are you sure you want to skip this exercise?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleCancelDialogCancel} color="primary" sx={{ color: '#787878' }}>
                        Keep trying
                    </Button>
                    <Button onClick={handleCancelDialogConfirm} color="primary" sx={{ color: '#787878' }}>
                        Skip
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Add Sentence Confirmation Dialog */}
            <AddSentenceDialog
                open={addSentenceDialogOpen}
                sentence={{
                    text: dialogPrompt.correct_vocabulary_word.audio_sentence || '',
                    id: dialogPrompt.correct_vocabulary_word.audio_sentence_id
                }}
                onClose={handleAddSentenceClose}
            />
        </Stack>
    );
};

export default DialogPromptQuizQuestionSpeakComponent;
